import React from 'react';

import cx from 'classnames';
import { isEmpty, isNil } from 'ramda';
import { Field, Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Icon from 'components/shared/Icon';
import Visibility from 'components/shared/Icon/icons/Visibility';
import Modal from 'components/shared/Modal';
import Slider from 'components/shared/Slider';
import FormTitle from 'components/shared/Typography/FormTitle';
import { closeModalAction, openModalAction } from 'store/modal';
import { isModalOpenedSelector, modalDataSelector } from 'store/modal/selectors';

import PopupActionButtons from '../../../PopupActionButtons';
import { useEditorContext } from '../../context';
import styles from './index.module.css';

const Rooms = React.forwardRef(({ property, onNextPage, entityMeta }, ref) => {
  const { handleAddRoomsToEntity, pages } = useEditorContext();
  const dispatch = useDispatch();
  const isNoData = isNil(property?.rooms) || isEmpty(property?.rooms);
  const isPreviewModalOpen = useSelector(isModalOpenedSelector('ROOM_PREVIEW'));
  const modalData = useSelector(modalDataSelector);
  const closeModal = () => {
    dispatch(closeModalAction());
  };
  const openModal = (roomIndex) => {
    dispatch(openModalAction({ type: 'ROOM_PREVIEW', data: { roomIndex } }));
  };

  const page = pages.find((page) => page.id === entityMeta?.id);

  if (page?.editedInProposal) {
    return (
      <div ref={ref} className={styles.container}>
        <div className={cx(styles.title, styles.longTitle)}>
          Heads up! You can't change rooms because you've edited the page within the proposal. If you need to change
          rooms, please delete the whole page, click Save, and add it again with the correct rooms.
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={styles.container}>
      <div className={styles.title}>Which room(s) would you like to add?</div>

      {isNoData && <div className={styles.noData}>There are no rooms</div>}
      {isNoData && <PopupActionButtons onNextPage={onNextPage} />}
      {!isNoData && (
        <Form
          onSubmit={({ rooms = [] } = {}) => {
            handleAddRoomsToEntity(
              property.rooms
                .filter((room) => rooms.includes(room.id))
                .map((room) => ({ id: room.id, name: room.name })),
            );
            onNextPage();
          }}
          initialValues={{ rooms: entityMeta?.rooms?.map((room) => room.id) }}
          render={({ handleSubmit }) => {
            return (
              <form onSubmit={handleSubmit}>
                <div className={styles.checkboxGroup}>
                  {property.rooms.map((room, idx) => (
                    <div key={room.id} className={styles.checkboxContainer}>
                      <Field
                        component={FormGroupAdapter}
                        name="rooms"
                        value={room.id}
                        type="checkbox"
                        labelText={room.name}
                        noMargin
                      />

                      <Icon
                        component={Visibility}
                        width="14px"
                        height="10px"
                        className={styles.previewButton}
                        onClick={() => openModal(idx)}
                      />
                    </div>
                  ))}
                </div>

                <PopupActionButtons isNextSubmitType />
              </form>
            );
          }}
        />
      )}

      <Modal isOpen={isPreviewModalOpen} toClose={closeModal} size="auto">
        {isPreviewModalOpen && (
          <div className={styles.sliderContainer}>
            <FormTitle center>{`${property.name} – ${property.rooms[modalData.roomIndex].name}`}</FormTitle>
            <Slider files={property.rooms[modalData.roomIndex].media} showLegend />
          </div>
        )}
      </Modal>
    </div>
  );
});

export default Rooms;
